import type { AABB, Vec2 } from '../../geo/types';
import { perimeterOf, radialArcOf } from './farRingSeam';

// Where along the bounds, counter-clockwise from their (minX, minY) corner, a land run starts and ends.
export interface ArcInterval {
  readonly from: number;
  readonly to: number;
}

export interface RunEnds {
  // How far along the bounds the rays from their centre through the segment from a to b pass from the
  // nearest end of a run, 0 if one lies between them.
  gap(a: Vec2, b: Vec2): number;
}

// A segment that crosses the ray the arc starts on spans the arc's origin, not the rest of the bounds.
function arcSpan(a: Vec2, b: Vec2, bounds: AABB, perimeter: number): readonly [number, number] {
  const [x, y] = [radialArcOf(a, bounds), radialArcOf(b, bounds)];
  const [lo, hi] = [Math.min(x, y), Math.max(x, y)];
  return hi - lo > perimeter / 2 ? [hi, lo + perimeter] : [lo, hi];
}

function firstAtOrAfter(sorted: readonly number[], value: number): number {
  let [lo, hi] = [0, sorted.length];
  while (lo < hi) {
    const middle = (lo + hi) >> 1;
    if (sorted[middle]! < value) lo = middle + 1;
    else hi = middle;
  }
  return lo;
}

function gapWithin(ends: readonly number[], lo: number, hi: number): number {
  const next = firstAtOrAfter(ends, lo);
  if (next < ends.length && ends[next]! <= hi) return 0;
  const after = next < ends.length ? ends[next]! - hi : Infinity;
  const before = next > 0 ? lo - ends[next - 1]! : Infinity;
  return Math.min(after, before);
}

// The refinement asks this of every edge it judges, so the ends are searched, not scanned.
export function createRunEnds(runs: readonly ArcInterval[], bounds: AABB): RunEnds {
  const perimeter = perimeterOf(bounds);
  const ends = runs
    .filter((run) => run.to > run.from)
    .flatMap((run) => [run.from, run.to])
    .sort((x, y) => x - y);
  return {
    gap(a, b) {
      const [lo, hi] = arcSpan(a, b, bounds, perimeter);
      return Math.min(
        gapWithin(ends, lo - perimeter, hi - perimeter),
        gapWithin(ends, lo, hi),
        gapWithin(ends, lo + perimeter, hi + perimeter)
      );
    },
  };
}
